import GetShuttleE2EOffersAction from "../../Actions/LogicActions/AddShuttleTransfer/GetShuttleE2EOffersAction";
import { ILegTrain } from "../../Orchestrator/Types/Index";
import { RideHailingMode } from "../../Orchestrator/Types/Interface/IRideHailing";
import { IShuttleStopOption, IShuttleTrip, ShuttleOffersStatus } from "../../Orchestrator/Types/Interface/IShuttleBus";

interface FetchArgs {
    stop: IShuttleStopOption;
    mode: RideHailingMode;
    trainLeg?: ILegTrain;
    passengers: { externalRef: string; age?: number; cards?: any[]; prmNeeds?: string[] }[];
    setTrips: (t: IShuttleTrip[] | null) => void;
    setStatus: (s: ShuttleOffersStatus) => void;
}

/**
 * The station endpoint of the shuttle hop, resolved from the typed ILegTrain
 * fields (no casts needed).
 *
 * Exported because shuttleTripsCache keys on it: a cache key that derived the
 * station any other way could drift from the request this module actually
 * sends, and serve one station's times under another's key.
 */
export function shuttleStationRef(mode: RideHailingMode, trainLeg?: ILegTrain): string {
    const ref =
        mode === "toStation"
            ? trainLeg?.origin?.stopPlaceRef
            : trainLeg?.destination?.stopPlaceRef;
    return ref || "";
}

/**
 * The end of the train leg this side's shuttle connects to: DEPARTURE for the
 * to-station side, ARRIVAL for the from-station side.
 *
 * ONE definition of that rule, shared by everything that needs it — the offers
 * request and cache key (shuttleDepartureDate, below) and the stale-shuttle
 * warning (shuttleTravelDate.shuttleDateMatch). They were briefly written
 * separately and had already drifted on the empty-string case; two functions
 * disagreeing about which end of a leg a shuttle belongs to is exactly the class
 * of bug this whole change set exists to remove.
 *
 * `||` not `??`: the constructors coalesce a missing `startTime`/`endTime` to ""
 * (booking.ts, GetLegsFromTripAction), so an empty string means "absent" here
 * and must fall through rather than be taken as a value. The shortXDateFormat
 * fields are left `undefined` instead, which `||` handles the same way.
 *
 * `shortEndDateFormat` is deliberately NOT in the arrival chain despite the
 * name: both constructors fill it from `end.serviceDeparture`, a DEPARTURE
 * field, so it is no more an arrival source than startTime is.
 *
 * That leaves a real ceiling on the arrival side — a leg with no `endTime`
 * falls back to the departure day, which on an overnight leg is exactly the
 * wrong-day search this side-awareness exists to prevent, and it degrades
 * silently. Unreachable today (`endTime` is always populated from
 * `end.serviceArrival.timetabledTime`); if that ever changes, the fix is to
 * align `shortEndDateFormat` with `serviceArrival` at both constructors and add
 * it here — not to widen this chain onto a departure field.
 */
export function trainDateForSide(
    mode: RideHailingMode,
    trainLeg?: ILegTrain
): string | undefined {
    return mode === "fromStation"
        ? trainLeg?.endTime || trainLeg?.startTime || trainLeg?.shortStartDateFormat
        : trainLeg?.startTime || trainLeg?.shortStartDateFormat;
}

/**
 * The trip DATE at local midnight (no timezone Z), matching the mobile request.
 * The time source may be a string, a Date, or a timestamp — normalize all of
 * them, deriving the date from LOCAL components to avoid a UTC day-shift.
 *
 * SIDE-DEPENDENT, and it has to be: the shuttle runs on the day it MEETS the
 * train, and on an overnight leg that is not the same calendar day at both
 * ends. Abu Dhabi -> Fujairah departs 23:31 and arrives 00:37 the NEXT day, so
 * the arrival-side shuttle (station -> stop) belongs to the arrival date. Using
 * the departure date there sold the previous day's bus: the modal listed
 * departures that had already run hours before the passenger landed, with
 * nothing under "Recommended Trips" because none of them fit. Where that day
 * has no service at all it is worse — TRIP_NO_SEARCH_RESULT, and since Fujairah
 * has exactly one shuttle stop the availability sweep hides the only stop and
 * the whole Shuttle Bus row with it. Mobile keys off the arrival time for the
 * same reason (e2e_shuttle_handler_mixin `_fetchAndEmitOffers`).
 *
 * Exported for the same reason as shuttleStationRef: it is part of the request
 * identity, so the cache must key on the exact value that gets sent. A
 * change-date moves the leg to a new day, and a key without the date would
 * serve the old day's shuttle times for the new one.
 */
export function shuttleDepartureDate(mode: RideHailingMode, trainLeg?: ILegTrain): string {
    // `unknown` deliberately: the typed field is a string, but this has always
    // accepted a Date or a timestamp from untyped callers and must keep doing so.
    const raw: unknown = trainDateForSide(mode, trainLeg);
    if (raw === null || raw === undefined || raw === "") return "";
    // Strings: keep the literal calendar date ("2026-07-01T05:55:00" -> "2026-07-01").
    if (typeof raw === "string") {
        const datePart = raw.split("T")[0];
        return /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? `${datePart}T00:00:00.000` : "";
    }
    // Date or timestamp: use local Y-M-D so the day matches what the user sees.
    const dt = raw instanceof Date ? raw : new Date(raw as number);
    if (Number.isNaN(dt.getTime())) return "";
    const y = dt.getFullYear();
    const m = `${dt.getMonth() + 1}`.padStart(2, "0");
    const d = `${dt.getDate()}`.padStart(2, "0");
    return `${y}-${m}-${d}T00:00:00.000`;
}

/**
 * Direction-aware helper that fetches shuttle offers for a selected stop.
 *
 * toStation:   rider travels FROM the shuttle stop TO the train origin station.
 *              fromStopRef = shuttle stop, toStopRef = train origin stopPlaceRef.
 * fromStation: rider travels FROM the train destination station TO the shuttle stop.
 *              fromStopRef = train destination stopPlaceRef, toStopRef = shuttle stop.
 *
 * Uncached by design — this is the raw call. Every display path goes through
 * shuttleTripsCache, which is now the ONLY caller: Confirm used to call this
 * directly to re-price for the final passenger set, and that re-fetch has been
 * removed, so the offerId booked is whichever one the cached sweep minted.
 */
export async function fetchShuttleOffersForStop(args: FetchArgs): Promise<IShuttleTrip[] | null> {
    const { stop, mode, trainLeg, passengers, setTrips, setStatus } = args;

    const stationRef = shuttleStationRef(mode, trainLeg);
    const departureDate = shuttleDepartureDate(mode, trainLeg);

    if (!stationRef) {
        return null;
    }

    setStatus("loading");

    const action = new GetShuttleE2EOffersAction();
    action.setParams({
        fromStopRef: mode === "toStation" ? stop.stopId : stationRef,
        toStopRef: mode === "toStation" ? stationRef : stop.stopId,
        departureDate,
        passengers,
    });

    await action.execute();
    const result = action.result;

    if (result === null) {
        setStatus("error");
        setTrips(null);
        return null;
    }

    setStatus("success");
    setTrips(result);
    return result;
}
